import type { BottleUnit, WeightUnit } from "@babia/checks";
import { entryEmoji, entryName } from "../../../lib/entry-text.ts";
import { changeIntro, changerName, type EntryChange, entryLine } from "../../../lib/revisions.ts";
import { Button } from "../../ui/button.tsx";
import { Sheet } from "../../ui/sheet.tsx";

/**
 * What an assistant changed in a row, before and now, with Put it back (#72).
 * Without `onPutBack` (a read-only log after the trial) it only shows.
 */
export function AssistantChangeSheet({
  change,
  unit,
  weightUnit,
  babyNameOf,
  now,
  putting,
  onClose,
  onPutBack,
}: {
  /** The change to show; null keeps the sheet closed. */
  change: EntryChange | null;
  unit: BottleUnit;
  weightUnit: WeightUnit;
  babyNameOf: (babyId: string) => string;
  now: Date;
  /** Put it back is on its way. */
  putting: boolean;
  onClose: () => void;
  onPutBack?: (change: EntryChange) => void;
}) {
  if (!change) return null;
  const after = change.after ?? change.before;
  // Name the babies only when the change moved the entry to the other one,
  // and the entry only when the change made it something else (a note's kind).
  const moved = change.before.babyId !== after.babyId;
  const named = entryName(change.before) !== entryName(after);
  const line = (side: typeof after) =>
    entryLine(side, unit, weightUnit, now, {
      ...(moved ? { babyName: babyNameOf(side.babyId) } : {}),
      named,
    });

  return (
    <Sheet
      open
      title={`Changed by ${changerName(change)}`}
      emoji={entryEmoji(after)}
      onClose={onClose}
    >
      <p className="mt-1 text-[14px] opacity-70">{changeIntro(change, now)}</p>
      {/* Two long notes can outgrow a phone: the comparison scrolls, the button stays. */}
      <dl className="mt-4 grid max-h-[45dvh] grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 overflow-y-auto overscroll-contain text-[15px]">
        <dt className="opacity-60">Before</dt>
        <dd className="min-w-0 break-words">{line(change.before)}</dd>
        <dt className="opacity-60">Now</dt>
        <dd className="min-w-0 break-words">{line(after)}</dd>
      </dl>
      {onPutBack ? (
        <Button
          variant="primary"
          className="mt-5 w-full"
          disabled={putting}
          onClick={() => onPutBack(change)}
        >
          {putting ? "Putting it back…" : "Put it back"}
        </Button>
      ) : null}
    </Sheet>
  );
}
